import React from 'react';
import { Alert, Button, Stack, Typography } from '@mui/material';
import type { IncidentReviewOutcome } from '../../services/incidentsApi';
import {
  groupedEvidenceReviewDecisionSummary,
  groupedPatternEvidenceDecisionSummary,
  PATTERN_EVIDENCE_NEEDS_MORE_EVIDENCE_LABEL,
  PATTERN_EVIDENCE_NOT_RECURRING_LABEL,
  PATTERN_EVIDENCE_SAFETY_ISSUE_CONFIRMED_LABEL,
} from '../../domain/incidents/groupedRepresentativeAuditCopy';
import {
  OBSERVATION_DISPOSITION_CONFIRM_TRUE_EVENT_LABEL,
  OBSERVATION_DISPOSITION_DECISION_RECORDED_UPDATE_HINT,
  OBSERVATION_DISPOSITION_DUPLICATE_LABEL,
  OBSERVATION_DISPOSITION_FALSE_POSITIVE_LABEL,
  OBSERVATION_DISPOSITION_NEEDS_MORE_REVIEW_LABEL,
} from '../../domain/incidents/observationDispositionCopy';
import {
  patternEvidenceCompleteCopy,
  patternOwnershipHasLinkedCase,
} from '../../domain/incidents/patternOwnershipContainment';
import type { IncidentDrawerDetailScope } from './incidentDrawerDetailTypes';

/** Epic 93f — representative-evidence decisions (existing resolve / close flows). */
export function GroupedGuidedVerifyEvidenceActions({ scope }: { scope: IncidentDrawerDetailScope }) {
  const s = scope as {
    canManageIncidentWorkflow?: boolean;
    drawerBusy?: string | null;
    resolveTargetIncidentId?: string;
    groupedRepresentativeReviewComplete?: boolean;
    groupedRepresentativeReviewOutcome?: string;
    observationDispositionMode?: boolean;
    observationDecisiveOutcomesBlocked?: boolean;
    observationDecisiveOutcomesBlockReason?: string;
    observationBackToCaseLabel?: string;
    observationParentCaseIncidentId?: string;
    observationNextIncidentId?: string | null;
    onOpenParentCaseReview?: () => void;
    onOpenNextObservationReview?: () => void;
    openResolveDialogWithOutcome?: (outcome: IncidentReviewOutcome) => void;
    handleMarkFalsePositive?: () => void;
    hasLinkedSafetyCase?: boolean;
    safetyCaseId?: string | null;
  };

  const observationDispositionMode = Boolean(s.observationDispositionMode);
  const hasRepresentative = Boolean(String(s.resolveTargetIncidentId || '').trim());
  const disabled = !s.canManageIncidentWorkflow || Boolean(s.drawerBusy) || !hasRepresentative;
  const reviewComplete = Boolean(s.groupedRepresentativeReviewComplete);
  const outcome = String(s.groupedRepresentativeReviewOutcome || '').trim().toLowerCase();
  const hasLinkedSafetyCase = Boolean(s.hasLinkedSafetyCase) || patternOwnershipHasLinkedCase(s.safetyCaseId);
  const decisionSummary = observationDispositionMode
    ? groupedEvidenceReviewDecisionSummary(outcome)
    : groupedPatternEvidenceDecisionSummary(outcome);
  const decisiveOutcomesBlocked = Boolean(observationDispositionMode && s.observationDecisiveOutcomesBlocked);
  const decisiveOutcomesBlockReason = String(s.observationDecisiveOutcomesBlockReason || '').trim();
  const testId = observationDispositionMode
    ? 'observation-disposition-actions'
    : 'grouped-verify-evidence-actions';

  if (reviewComplete && decisionSummary) {
    const confirmed = outcome === 'true_event';
    const showObservationNextSteps = observationDispositionMode;
    return (
      <Stack spacing={1.25} data-testid={testId}>
        <Alert
          severity={confirmed ? 'success' : 'info'}
          sx={{ borderRadius: 2 }}
          data-testid="grouped-verify-evidence-decision-recorded"
        >
          <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.45 }}>
            {decisionSummary}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.5 }}>
            {observationDispositionMode
              ? OBSERVATION_DISPOSITION_DECISION_RECORDED_UPDATE_HINT
              : confirmed
                ? patternEvidenceCompleteCopy(hasLinkedSafetyCase)
                : 'This decision is recorded on the representative evidence sample. Update it below if your review changes.'}
          </Typography>
        </Alert>
        {showObservationNextSteps ? (
          <Stack spacing={1} data-testid="observation-disposition-next-steps">
            {s.onOpenParentCaseReview && String(s.observationParentCaseIncidentId || '').trim() ? (
              <Button
                variant="contained"
                disabled={disabled}
                onClick={s.onOpenParentCaseReview}
                sx={{ fontWeight: 800, alignSelf: 'flex-start' }}
                data-testid="observation-back-to-case-action"
              >
                {String(s.observationBackToCaseLabel || 'Back to pattern').trim()}
              </Button>
            ) : null}
            {s.onOpenNextObservationReview && String(s.observationNextIncidentId || '').trim() ? (
              <Button
                variant="outlined"
                disabled={disabled}
                onClick={s.onOpenNextObservationReview}
                sx={{ fontWeight: 700, alignSelf: 'flex-start' }}
                data-testid="observation-next-observation-action"
              >
                Next observation
              </Button>
            ) : null}
            {s.canManageIncidentWorkflow ? (
              <Button
                variant="text"
                size="small"
                disabled={disabled}
                onClick={() => s.openResolveDialogWithOutcome?.(outcome as IncidentReviewOutcome)}
                sx={{ fontWeight: 700, alignSelf: 'flex-start', px: 0.5 }}
                data-testid="observation-update-review-decision-action"
              >
                Update review decision
              </Button>
            ) : null}
          </Stack>
        ) : s.canManageIncidentWorkflow ? (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Button
              variant="outlined"
              size="small"
              disabled={disabled}
              onClick={() => s.openResolveDialogWithOutcome?.(outcome as IncidentReviewOutcome)}
              sx={{ fontWeight: 700 }}
            >
              Update review decision
            </Button>
          </Stack>
        ) : null}
      </Stack>
    );
  }

  if (!observationDispositionMode) {
    return (
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        sx={{ mt: 0, mb: 0 }}
        data-testid={testId}
      >
        <Button
          variant="outlined"
          disabled={disabled}
          onClick={() => s.openResolveDialogWithOutcome?.('true_event')}
          sx={{ fontWeight: 700 }}
        >
          {PATTERN_EVIDENCE_SAFETY_ISSUE_CONFIRMED_LABEL}
        </Button>
        <Button
          variant="outlined"
          disabled={disabled}
          onClick={() => s.openResolveDialogWithOutcome?.('insufficient_evidence')}
          sx={{ fontWeight: 700 }}
        >
          {PATTERN_EVIDENCE_NEEDS_MORE_EVIDENCE_LABEL}
        </Button>
        <Button
          variant="outlined"
          disabled={disabled}
          onClick={() => s.openResolveDialogWithOutcome?.('not_actionable')}
          sx={{ fontWeight: 700 }}
        >
          {PATTERN_EVIDENCE_NOT_RECURRING_LABEL}
        </Button>
      </Stack>
    );
  }

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      useFlexGap
      flexWrap="wrap"
      sx={{ mt: 0, mb: 0 }}
      data-testid={testId}
    >
      {decisiveOutcomesBlocked && decisiveOutcomesBlockReason ? (
        <Alert severity="warning" sx={{ width: '100%', borderRadius: 2 }} data-testid="observation-evidence-gate-alert">
          <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.45 }}>
            {decisiveOutcomesBlockReason}
          </Typography>
        </Alert>
      ) : null}
      <Button
        variant="outlined"
        disabled={disabled || decisiveOutcomesBlocked}
        onClick={() => s.openResolveDialogWithOutcome?.('true_event')}
        sx={{ fontWeight: 700 }}
      >
        {OBSERVATION_DISPOSITION_CONFIRM_TRUE_EVENT_LABEL}
      </Button>
      <Button
        variant="outlined"
        disabled={disabled}
        onClick={() => s.openResolveDialogWithOutcome?.('insufficient_evidence')}
        sx={{ fontWeight: 700 }}
      >
        {OBSERVATION_DISPOSITION_NEEDS_MORE_REVIEW_LABEL}
      </Button>
      <Button
        variant="outlined"
        color="warning"
        disabled={disabled || decisiveOutcomesBlocked}
        onClick={s.handleMarkFalsePositive}
        sx={{ fontWeight: 700 }}
      >
        {OBSERVATION_DISPOSITION_FALSE_POSITIVE_LABEL}
      </Button>
      <Button
        variant="outlined"
        disabled={disabled || decisiveOutcomesBlocked}
        onClick={() => s.openResolveDialogWithOutcome?.('duplicate')}
        sx={{ fontWeight: 700 }}
      >
        {OBSERVATION_DISPOSITION_DUPLICATE_LABEL}
      </Button>
    </Stack>
  );
}
